import { Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  ControlTextField,
  FormNumberSelect,
  FormTextField,
} from '@schooly/components/form-text-field';
import { DeleteIcon, DragIcon, InformationIcon, LockIcon } from '@schooly/style';
import { getControllerErrorText } from '@schooly/utils/get-controller-error-text';
import { FC, useEffect } from 'react';
import { useDrag, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Controller, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { AssessmentsGradeForm, GRADE_OPTIONS_DND_TYPE } from './SchoolAssessmentsGradeModalContent';

const MIN_COUNT = 0;
const MAX_COUNT = Number.MAX_SAFE_INTEGER;

type SchoolAssessmentsGradeOptionRowProps = {
  id: string;
  index: number;
  validateOptionValue: (value: number | null) => string | boolean;
  deleteOption?: (index: number) => () => void;
};

export const SchoolAssessmentsGradeOptionRow: FC<SchoolAssessmentsGradeOptionRowProps> = ({
  id,
  index,
  validateOptionValue,
  deleteOption,
}) => {
  const { control, watch, trigger } = useFormContext<AssessmentsGradeForm>();
  const { formatMessage } = useIntl();
  const options = watch('options');

  const [{ isDragging: isDraggingItem, item }, drag, preview] = useDrag({
    type: GRADE_OPTIONS_DND_TYPE,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
    }),
  });
  const itemIndex = item ? item.index : index;

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));
  //errors are not handled as expected when dragging
  const showError = !isDragging;

  //to hide the default drag preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  useEffect(() => {
    const { unsubscribe } = watch((_, { name }) => {
      if (!name) {
        return;
      }

      if (name === `options.${itemIndex}.value`) {
        options.forEach((o, i) => {
          if (o.value === null && i !== itemIndex) return;
          trigger(`options.${i}.value`);
        });
      }
    });

    return () => unsubscribe();
  }, [watch, itemIndex, trigger, options]);

  return (
    <Stack
      direction="row"
      gap={1}
      sx={(theme) => ({
        width: '100%',
        opacity: isDraggingItem ? 0 : 1,
        '& .MuiTextField-root': { bgcolor: 'background.paper', borderRadius: theme.spacing(1) },
      })}
    >
      {options.length > 1 && (
        <IconButton inverse ref={drag} sx={{ height: 44 }}>
          <DragIcon />
        </IconButton>
      )}

      <Stack gap={1} width="100%">
        <Stack direction="row" gap={1}>
          <Controller
            control={control}
            name={`options.${itemIndex}.name`}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              const error = fieldState.error;
              const hasError = !!error;
              const errorMessage = getControllerErrorText(error, undefined, formatMessage);
              const disabled = options[itemIndex]?.has_assessments;

              return (
                <FormTextField
                  disabled={disabled}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  label={formatMessage({ id: 'assessments-GradeName' })}
                  fullWidth
                  canClear={!disabled}
                  error={showError && hasError}
                  helperText={showError && errorMessage}
                />
              );
            }}
          />
          <FormNumberSelect
            name={`options.${itemIndex}.value`}
            min={MIN_COUNT}
            max={MAX_COUNT}
            rules={{ required: true, validate: (value) => validateOptionValue(value) }}
            label={formatMessage({ id: 'assessments-Value' })}
            hiddenLabel={false}
            showEndAdornment={true}
            hideError={!showError}
            fullWidth
            canClear
            required
          />
        </Stack>
        <ControlTextField
          name={`options.${itemIndex}.description`}
          control={control}
          label={formatMessage({ id: 'assessments-AddGradeOptionDescription' })}
          fullWidth
          customIcon={
            <Tooltip
              componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
              title={formatMessage({ id: 'assessments-AddGradeOptionDescriptionTooltip' })}
            >
              <Icon
                sx={{ color: 'common.grey', '&:hover': { color: 'primary.main' }, cursor: 'auto' }}
              >
                <InformationIcon />
              </Icon>
            </Tooltip>
          }
        />
      </Stack>
      {options.length > 1 &&
        (deleteOption ? (
          <IconButton inverse onClick={deleteOption(index)} sx={{ height: 44 }}>
            <DeleteIcon />
          </IconButton>
        ) : (
          <Tooltip
            title={
              <>
                <Typography mb={2}>
                  {formatMessage({ id: 'assessments-GradeOptionDelete' })}
                </Typography>
                <Typography>
                  {formatMessage({ id: 'assessments-GradeOptionDelete-Description' })}
                </Typography>
              </>
            }
            componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
          >
            <IconButton inverse sx={{ height: 44, '&.MuiButtonBase-root': { cursor: 'default' } }}>
              <LockIcon />
            </IconButton>
          </Tooltip>
        ))}
    </Stack>
  );
};
